import React, { useState, useEffect } from 'react';
import PromptHistoryService, { PromptHistoryItem } from '../services/promptHistoryService';



interface PromptHistoryPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onPromptSelect: (query: string) => void;
  userId: string;
}

export const PromptHistoryPopup: React.FC<PromptHistoryPopupProps> = ({
  isOpen,
  onClose,
  onPromptSelect,
  userId
}) => {
  const [history, setHistory] = useState<PromptHistoryItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadHistory();
    }
  }, [isOpen, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('Loading prompt history for user:', userId);
      const historyData = await PromptHistoryService.getHistory(userId, 50, 0);
      console.log('Loaded prompt history:', historyData.length, 'items');
      setHistory(historyData);
    } catch (err) {
      console.error('Error loading history:', err);
      setError(`Failed to load prompt history: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // The service already handles fallback to local storage
      setHistory([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredHistory = history.filter(item =>
    item.query.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePromptClick = (query: string) => {
    onPromptSelect(query);
    onClose();
  };

  const handleCopyQuery = (query: string, event: React.MouseEvent) => {
    event.stopPropagation();
    navigator.clipboard.writeText(query).then(() => {
      // Could add a toast notification here in the future
      console.log('Query copied to clipboard:', query);
    }).catch((err) => {
      console.error('Failed to copy query:', err);
    });
  };

  const handleClearHistory = () => {
    if (window.confirm('Are you sure you want to clear all prompt history?')) {
      PromptHistoryService.clearHistory();
      setHistory([]);
    }
  };

  const handleRemoveItem = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    // Only remove from local storage (API items can't be deleted from client)
    PromptHistoryService.removeFromHistory(id);
    // Remove from current display regardless of source
    setHistory(prev => prev.filter(item => item.id !== id));
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diffInHours = (now.getTime() - timestamp.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now.getTime() - timestamp.getTime()) / (1000 * 60));
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return timestamp.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 pt-20"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[70vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-2 rounded-xl">
                <span className="material-symbols-outlined text-white" style={{ fontSize: '20px' }}>
                  history
                </span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
                  Prompt History
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {history.length} saved prompt{history.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={loadHistory}
                disabled={isLoading}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
                title="Refresh history"
              >
                <span className={`material-symbols-outlined text-slate-400 ${isLoading ? 'animate-spin' : ''}`}>
                  refresh
                </span>
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                <span className="material-symbols-outlined text-slate-400">
                  close
                </span>
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              search
            </span>
            <input
              type="text"
              placeholder="Search prompts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="bg-slate-100 dark:bg-slate-800 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin"></div>
              </div>
              <h4 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
                Loading prompt history...
              </h4>
              <p className="text-slate-500 dark:text-slate-400">
                Fetching your previous prompts
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="bg-red-100 dark:bg-red-900/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="material-symbols-outlined text-red-500" style={{ fontSize: '32px' }}>
                  error
                </span>
              </div>
              <h4 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
                Failed to load history
              </h4>
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                {error}
              </p>
              <button
                onClick={loadHistory}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-slate-100 dark:bg-slate-800 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="material-symbols-outlined text-slate-400" style={{ fontSize: '32px' }}>
                  {searchTerm ? 'search_off' : 'history'}
                </span>
              </div>
              <h4 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
                {searchTerm ? 'No matching prompts found' : 'No prompt history yet'}
              </h4>
              <p className="text-slate-500 dark:text-slate-400">
                {searchTerm 
                  ? 'Try adjusting your search terms'
                  : 'Your previous prompts will appear here for easy reuse'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredHistory.map((item) => (
                <div
                  key={item.id}
                  className="group p-4 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg cursor-pointer transition-all duration-200 border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
                  onClick={() => handlePromptClick(item.query)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 mr-3">
                      <p className="text-slate-800 dark:text-slate-200 font-medium mb-1 overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {item.query}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {formatTimestamp(item.timestamp)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-all">
                      <button
                        onClick={(e) => handleCopyQuery(item.query, e)}
                        className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-400 hover:text-blue-500 transition-all"
                        title="Copy query"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                          content_copy
                        </span>
                      </button>
                      <button
                        onClick={(e) => handleRemoveItem(item.id, e)}
                        className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-400 hover:text-red-500 transition-all"
                        title="Remove from history"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                          delete
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {history.length > 0 && (
          <div className="p-6 border-t border-slate-200 dark:border-slate-700">
            <button
              onClick={handleClearHistory}
              className="w-full py-2 px-4 bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400 rounded-lg transition-colors font-medium"
            >
              Clear All History
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptHistoryPopup;